import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import { CircularProgress } from '@mui/material';
import * as stateActions from '../../../actions/stateActions';
import avatarPlaceholder from '../../../avatar-placeholder.png';

const VideoStyled = styled('video')({
	objectFit: 'cover',
	width: '100%',
	height: '100%'
});

const ContainerStyled = styled('div')({
	position: 'absolute',  // Changed to absolute positioning for better control
	width: '100%',
	height: '100%',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	backgroundColor: '#000',
});

const AvatarStyled = styled('img')({
	objectFit: 'contain',
	width: '30%',
	height: '30%',
	maxWidth: '200px',
	maxHeight: '200px',
	borderRadius: '50%'
});

	const MediaView = (props) => {
	const { mediaStream, showAvatar, showBlank, isLocal, isMainScreen = false } = props;
	const videoRef = useRef(null);
	const [, setForceUpdate] = useState(0);
	const dispatch = useDispatch();
	const avatarImageUri = useSelector(state => state.user.avatarImageUri);
	const cameraLoading = useSelector(state => state.user.cameraLoading);
	// const videoEnabled = useSelector(state => state.user.videoEnabled);

	// Use uploaded avatar if available, otherwise use default placeholder
	const defaultAvatarSrc = typeof avatarPlaceholder === "string"
		? avatarPlaceholder
		: avatarPlaceholder?.default || avatarPlaceholder;
	const avatarSrc = avatarImageUri || defaultAvatarSrc;


	// Monitor track state changes to detect when remote user turns off camera
	// Also clear camera loading state when video becomes ready (for local video)
	useEffect(() => {
		if (!mediaStream) return;

		const tracks = mediaStream.getVideoTracks();
		if (tracks.length === 0) return;

		// Use refs to track current state to avoid stale closures
		const cameraLoadingRef = { current: cameraLoading };
		const checkTrackState = () => {
			// Force re-render to update UI
			setForceUpdate(prev => prev + 1);
			
			// For local video, clear loading state when video becomes ready
			if (isLocal && cameraLoadingRef.current) {
				const hasReadyVideo = tracks.some(track => 
					track.readyState === 'live' && track.enabled && !track.muted
				);
				if (hasReadyVideo) {
					cameraLoadingRef.current = false;
					dispatch(stateActions.setCameraLoading({ cameraLoading: false }));
				}
			}
		};

		// Listen to track events
		tracks.forEach(track => {
			track.addEventListener('mute', checkTrackState);
			track.addEventListener('unmute', checkTrackState);
			track.addEventListener('ended', checkTrackState);
			track.addEventListener('started', checkTrackState); // When track becomes live
		});

		// Also check periodically in case events don't fire
		const interval = setInterval(checkTrackState, 200); // Check more frequently
		// Initial check
		checkTrackState();

		return () => {
			clearInterval(interval);
			tracks.forEach(track => {
				track.removeEventListener('mute', checkTrackState);
				track.removeEventListener('unmute', checkTrackState);
				track.removeEventListener('ended', checkTrackState);
				track.removeEventListener('started', checkTrackState);
			});
		};
		// Remove forceUpdate from dependencies - it's only used to trigger re-renders, not to re-run the effect
	}, [mediaStream, isLocal, cameraLoading, dispatch]);

	// Determine if we should show the avatar placeholder
	// Check for: no stream, no video tracks, disabled tracks, muted tracks, or ended/stopped tracks
	const videoTracks = mediaStream?.getVideoTracks?.() || [];

	// For local video, check enabled state; for remote, check muted state
	const hasValidVideo = isLocal
		? videoTracks.length > 0 && videoTracks.some(track => track.readyState === 'live' && track.enabled)
		: videoTracks.length > 0 && videoTracks.some(track => track.readyState === 'live' && !track.muted);

	// If showBlank -> NEVER show avatar (even if no valid video)
	const shouldShowBlank = Boolean(showBlank);

	// If blank -> no avatar fallback
	// Show loader instead of avatar when camera is loading (only on main screen, not on local preview)
	// const hasTracksButNotReady = videoTracks.length > 0 && 
	// 	!videoTracks.some(track => track.readyState === 'live' && (isLocal ? track.enabled : !track.muted));
	
	// Show loader only on main screen when local camera is loading (not on local preview)
	const shouldShowLoader = isMainScreen && cameraLoading && !hasValidVideo;
	const shouldShowAvatar = shouldShowBlank || shouldShowLoader ? false : (showAvatar || !hasValidVideo);

	useEffect(() => {
		if (!videoRef.current) return;
	
		// If blank OR avatar -> clear video immediately
		if (shouldShowBlank || shouldShowAvatar || !mediaStream || !hasValidVideo) {
		  if (videoRef.current.srcObject) {
			videoRef.current.pause();
			videoRef.current.srcObject = null;
			videoRef.current.src = '';
			videoRef.current.load();
		  }
		  return;
		}
	
		// Otherwise play video
		videoRef.current.srcObject = mediaStream;
		videoRef.current.play().catch(err => console.warn('Video play failed:', err));
	  }, [mediaStream, shouldShowBlank, shouldShowAvatar, hasValidVideo]);

	return (
		<ContainerStyled style={{ backgroundColor: '#000' }}>
      {/* Blank black state */}
      {shouldShowBlank && (
        <div style={{ width: '100%', height: '100%', backgroundColor: '#000' }} />
      )}

      {/* Loader when camera is loading (only for local video) */}
      {shouldShowLoader && (
        <CircularProgress 
          size={60} 
          thickness={4}
          sx={{
            color: '#fff',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
        }}
        />
      )}
      {/* Avatar only if NOT blank and NOT loading */}
      {!shouldShowBlank && !shouldShowLoader && shouldShowAvatar && (
        <AvatarStyled src={avatarSrc} alt="Avatar placeholder" />
      )}
      <VideoStyled
        ref={videoRef}
        autoPlay
        playsInline
        style={{ display: (shouldShowBlank || shouldShowAvatar || shouldShowLoader) ? 'none' : 'block' }}
      />
    </ContainerStyled>
	);
};

MediaView.propTypes =
{
	mediaStream : PropTypes.any,
	showAvatar : PropTypes.bool,
	showBlank : PropTypes.bool,
	isLocal : PropTypes.bool,
	isMainScreen : PropTypes.bool
};

export default MediaView;